import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { quotationsApi as Q, QUOTE_STATUS_STYLE, inr } from "@/lib/quotations";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Loader2 } from "lucide-react";

export default function Quotations() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ statuses: [] });
  const [stats, setStats] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [s, list] = await Promise.all([Q.stats(), Q.list({ search: search || undefined, status })]);
      setStats(s); setRows(list);
    } finally { setLoading(false); }
  };
  useEffect(() => { Q.meta().then(setMeta); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  return (
    <div className="space-y-6" data-testid="quotations-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Quotations</h2>
          <p className="mt-1 text-sm text-slate-500">Commercial proposals to customers. Only Accepted quotations become Sales Orders.</p>
        </div>
        {can("quotations", "create") && (
          <Button onClick={() => navigate("/quotations/new")} data-testid="new-quotation-btn" className="bg-indigo-600 text-white hover:bg-indigo-700">
            <Plus className="mr-2 h-4 w-4" />New Quotation
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className="p-4" data-testid="quote-stat-total"><p className="text-xs uppercase tracking-wider text-slate-500">Total</p><p className="mt-1 font-heading text-2xl font-bold text-slate-900">{stats.total ?? 0}</p></Card>
        <Card className="p-4" data-testid="quote-stat-open"><p className="text-xs uppercase tracking-wider text-slate-500">Open</p><p className="mt-1 font-heading text-2xl font-bold text-blue-600">{stats.open ?? 0}</p></Card>
        <Card className="p-4" data-testid="quote-stat-accepted"><p className="text-xs uppercase tracking-wider text-slate-500">Accepted</p><p className="mt-1 font-heading text-2xl font-bold text-emerald-600">{stats.Accepted ?? 0}</p></Card>
        <Card className="p-4" data-testid="quote-stat-value"><p className="text-xs uppercase tracking-wider text-slate-500">Accepted value</p><p className="mt-1 font-heading text-xl font-bold text-slate-900">{inr(stats.accepted_value)}</p></Card>
      </div>

      <Card className="p-4">
        <div className="mb-4 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search quote no / customer / enquiry" className="pl-9" data-testid="quote-search-input" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-48" data-testid="quote-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{meta.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase text-slate-500">Quote No</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Customer</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Items</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Grand total</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Validity</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate(`/quotations/${r.id}`)} data-testid={`quotation-row-${r.quote_no}`}>
                    <TableCell className="font-medium text-slate-800">{r.quote_no}{r.enquiry_no ? <span className="block text-xs text-slate-400">from {r.enquiry_no}</span> : null}</TableCell>
                    <TableCell className="text-slate-700">{r.customer?.company_name}</TableCell>
                    <TableCell className="text-slate-600">{(r.items || []).length}</TableCell>
                    <TableCell className="tabular-nums font-medium text-slate-800">{inr(r.totals?.grand_total)}</TableCell>
                    <TableCell className="text-slate-600">{r.validity_date || "—"}</TableCell>
                    <TableCell><Badge className={`hover:bg-inherit ${QUOTE_STATUS_STYLE[r.status]}`}>{r.status}</Badge></TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && <TableRow><TableCell colSpan={6} className="py-12 text-center text-slate-400" data-testid="quote-empty">No quotations yet. Create one from an enquiry or a customer.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
